/**
 * 医生工作台 私信管理相关接口
 * 接口文档：https://kapi.corp.kuaishou.com/project/10525/interface/api/838638
 * @author xianghai
 */
import {
  IClinicInfo,
  IClinicInfoDict,
  IGetClinicInfoResult,
  IMainMessageAutoReply,
  IMainMessageDiseaseVideo
} from '@/schemas/main-message';
import { checkResponse } from '@/utils/helpers';
import { request } from './request';
import { ApiConfigs, Response } from '../schemas/service';

const api: ApiConfigs = {
  editMainMessageSwitch: {
    url: '/health/api/v1/doctorEnd/msg/private-msg-switch',
    method: 'POST'
  },
  getMainMessageAutoReply: {
    url: '/health/api/v1/doctorEnd/msg/auto-reply',
    method: 'GET'
  },
  addMainMessageAutoReply: {
    url: '/health/api/v1/doctorEnd/msg/auto-reply',
    method: 'POST'
  },
  editMainMessageAutoReply: {
    url: '/health/api/v1/doctorEnd/msg/auto-reply',
    method: 'PUT'
  },
  deleteMainMessageAutoReply: {
    url: '/health/api/v1/doctorEnd/msg/auto-reply',
    method: 'DELETE'
  },
  getClinicInfoDict: {
    url: '/health/api/v2/visit/dict',
    method: 'GET'
  },
  getMainMessageClinicInfo: {
    url: '/health/api/v2/visit/time-info',
    method: 'GET'
  },
  editMainMessageClinicInfo: {
    url: '/health/api/v2/visit/time-info',
    method: 'POST'
  },
  getMainMessageDiseaseVideo: {
    url: '/health/api/v1/doctorEnd/msg/video',
    method: 'GET'
  },
  addMainMessageDiseaseVideo: {
    url: '/health/api/v1/doctorEnd/msg/video',
    method: 'POST'
  },
  editMainMessageDiseaseVideo: {
    url: '/health/api/v1/doctorEnd/msg/video',
    method: 'PUT'
  },
  deleteMainMessageDiseaseVideo: {
    url: '/health/api/v1/doctorEnd/msg/video',
    method: 'DELETE'
  },
  getMainMessageOverView: {
    url: '/health/api/v1/doctorEnd/msg/home',
    method: 'GET'
  },
  getMainMessageMsgList: {
    url: '/health/api/v1/doctorEnd/msg/list',
    method: 'GET'
  }
};

interface IGetAutoReplyResponse {
  max: number;
  replies: IMainMessageAutoReply[];
}

interface IGetDiseaseVideoResponse {
  videos: IMainMessageDiseaseVideo[];
  hasMore: boolean;
  nextOffset: number;
}

const mainMessage = {
  async editMainMessageSwitch(open: boolean) {
    try {
      const response: Response<boolean> = await request(
        api.editMainMessageSwitch,
        undefined,
        { open }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getMainMessageAutoReply() {
    try {
      const response: Response<IGetAutoReplyResponse> = await request(
        api.getMainMessageAutoReply
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async addMainMessageAutoReply(params: { title: string; reply: string }) {
    try {
      const response: Response = await request(
        api.addMainMessageAutoReply,
        undefined,
        params
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async editMainMessageAutoReply(data: {
    id: string;
    title: string;
    reply: string;
  }) {
    try {
      const response: Response = await request(
        api.editMainMessageAutoReply,
        undefined,
        data
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async deleteMainMessageAutoReply(id: string) {
    try {
      const response: Response<number> = await request(
        api.deleteMainMessageAutoReply,
        undefined,
        { id }
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getClinicInfoDict() {
    try {
      const response: Response<IClinicInfoDict> = await request(
        api.getClinicInfoDict,
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getMainMessageClinicInfo() {
    try {
      const response: Response<IGetClinicInfoResult> = await request(
        api.getMainMessageClinicInfo,
      );
      const { result } = response;
      const { visitInfo, specialInfo } = result;
      return {
        clinicInfo: visitInfo,
        specialInfo,
      }
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async editMainMessageClinicInfo(visitInfo: IClinicInfo[]) {
    try {
      const response: Response = await request(
        api.editMainMessageClinicInfo,
        undefined,
        {
          visitInfo
        }
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getMainMessageDiseaseVideo(offset: number) {
    try {
      const response: Response<IGetDiseaseVideoResponse> = await request(
        api.getMainMessageDiseaseVideo,
        { offset }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async addMainMessageDiseaseVideo(data: {
    title: string;
    photoId: string;
    photoPicUrl: string;
  }) {
    try {
      const response: Response<string> = await request(
        api.addMainMessageDiseaseVideo,
        undefined,
        data
      );
      const { result = '' } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async editMainMessageDiseaseVideo(data: {
    id: string;
    title: string;
    photoId: string;
    photoPicUrl: string;
  }) {
    try {
      const response: Response = await request(
        api.editMainMessageDiseaseVideo,
        undefined,
        data
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async deleteMainMessageDiseaseVideo(id: string) {
    try {
      const response: Response<number> = await request(
        api.deleteMainMessageDiseaseVideo,
        undefined,
        { id }
      );
      const { code, result = '' } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getMainMessageOverview() {
    try {
      const response: Response<number> = await request(
        api.getMainMessageOverview
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getMainMessageMsgList() {
    try {
      const response: Response<number> = await request(
        api.getMainMessageMsgList
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default mainMessage;
